class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
 
      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 800px 0px' }
  );
 
  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
 
      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;
 
      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });
 
  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};
 
  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;
 
  connectedCallback() {
    // If already in viewport when connected (e.g. after Section Rendering re-inserts the element),
    // trigger the load directly instead of waiting for IntersectionObserver threshold crossing.
    const rect = this.getBoundingClientRect();
    const inViewport = rect.top < window.innerHeight + 800 && rect.bottom > -800;
    if (inViewport && this.dataset.recommendationsPerformed !== 'true') {
      this.#loadRecommendations();
    } else {
      this.#intersectionObserver.observe(this);
    }
    this.#mutationObserver.observe(this, { attributes: true });
  }
 
  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;
 
    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }
 
    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    // Exception: if performed=true but there's no real content (e.g. after Section Rendering replaces
    // the DOM), force a re-fetch to repopulate the carousel.
    const hasRealContent = this.querySelector('.resource-list__item:not(.product-recommendations__skeleton-item)') !== null;
    if (recommendationsPerformed === 'true' && hasRealContent) {
      return;
    }
 
    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }
 
        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);
 
        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          // Inicializar botones "Agregar" de product-recommendations
          initRecAddButtons(this);
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }
 
  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
 
    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }
 
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
 
    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }
 
      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }
 
  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}
 
if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
 
// ── STANLEY: actualización de precio tachado + badge de descuento en cards ───
// Definido aquí (asset cargado en el PDP) porque el <script> inline de
// custom-variant-swatches.liquid NO se ejecuta cuando product-recommendations.js
// inyecta el HTML via innerHTML. Se expone en window para que cualquier handler
// (server-render o AJAX) lo reutilice.
function stanleyFormatMoneyAR(cents) {
  return '$' + Math.round(cents / 100).toLocaleString('es-AR');
}
 
// STANLEY: actualiza el bloque de cuotas (installments-price) de la card segun el
// precio de la variante. Si la variante tiene descuento por metafield, calcula la
// cuota sobre el precio con descuento; si no, sobre el precio lleno. Replica la
// matematica de installments-price.liquid (division entera). Identica a la del
// snippet custom-variant-swatches.liquid para mantener ambas copias en sincro.
function stanleyUpdateCardInstallments(card, discountPct, priceCents) {
  const inst = card.querySelector('.installments-price');
  if (!inst) return;
  const valEl = inst.querySelector('.installments-price__value');
  if (!valEl) return;
 
  let count = parseInt(inst.dataset.installments || '', 10);
  if (!count || count < 1) {
    const m = (inst.textContent || '').match(/(\d+)\s+cuota/i);
    count = m ? parseInt(m[1], 10) : 6;
  }
  if (!priceCents || priceCents < 1) return;
 
  const baseCents = (discountPct > 0)
    ? Math.round(priceCents * (100 - discountPct) / 100)
    : priceCents;
  valEl.textContent = stanleyFormatMoneyAR(Math.floor(baseCents / count));
}
 
function stanleyUpdateCardPrice(card, variantId) {
  if (!card || !variantId) return;
 
  const swatch      = card.querySelector('.card-swatches__swatch[data-variant-id="' + variantId + '"]');
  const discountPct = swatch ? parseInt(swatch.dataset.discountPercent || '0', 10) : 0;
  const priceCents  = swatch ? parseInt(swatch.dataset.variantPrice   || '0', 10) : 0;
 
  // STANLEY: recalcular el precio en cuotas segun la variante (antes del early-return
  // de priceContainers para que siempre actualice aunque no haya priceContainer).
  stanleyUpdateCardInstallments(card, discountPct, priceCents);
 
  // La tarjeta puede tener múltiples priceContainers (desktop + carousel mobile)
  const priceContainers = card.querySelectorAll('[ref="priceContainer"]');
  if (!priceContainers.length) return;
 
  let imgBadge = card.querySelector('.stanley-card-discount-badge');
 
  priceContainers.forEach((priceContainer) => {
    // Limpiar lo previo (Liquid + JS)
    priceContainer.querySelectorAll('.stanley-discounted-price, .stanley-discount-badge')
      .forEach((el) => el.remove());
 
    // Restaurar precio original (quitar tachado/estilos)
    const origSpan = priceContainer.querySelector('.price');
    if (origSpan) {
      origSpan.style.removeProperty('text-decoration');
      origSpan.style.removeProperty('color');
      origSpan.style.removeProperty('font-size');
      // STANLEY FIX: el texto de origSpan quedaba stale (precio de la variante
      // con la que cargó la página originalmente) porque antes solo se
      // togglaba el estilo, nunca el texto. Si la nueva variante tiene un
      // precio distinto, recalcularlo acá evita mostrar el número equivocado
      // o, en algunos casos de DOM, dejar el precio en blanco.
      if (priceCents > 0) {
        origSpan.textContent = stanleyFormatMoneyAR(priceCents);
      }
    }
 
    if (discountPct > 0 && priceCents > 0 && origSpan) {
      const discountedCents = Math.round(priceCents * (1 - discountPct / 100));
 
      origSpan.style.setProperty('text-decoration', 'line-through');
      origSpan.style.setProperty('color', 'rgba(0,0,0,0.4)');
      origSpan.style.setProperty('font-size', '0.85em');
 
      const discEl = document.createElement('span');
      discEl.className = 'price stanley-discounted-price';
      discEl.textContent = stanleyFormatMoneyAR(discountedCents);
      discEl.style.cssText = 'font-weight:500; margin-left:6px;';
      priceContainer.appendChild(discEl);
    }
  });
 
  // Badge sobre la imagen (uno por card). El div .stanley-card-discount-badge
  // SIEMPRE existe en el DOM (ver custom-product-card-badges.liquid), oculto
  // con [hidden] cuando no aplica. Acá solo se togglean clases/atributo — nunca
  // se crea ni inserta un nodo nuevo en .card-gallery (eso rompía el carrusel).
  if (discountPct > 0 && priceCents > 0) {
    if (imgBadge) {
      imgBadge.classList.add('product-badges__badge', 'product-badges__badge--sale');
      imgBadge.removeAttribute('hidden');
      imgBadge.textContent = '-' + discountPct + '%';
    }
  } else if (imgBadge) {
    imgBadge.classList.remove('product-badges__badge', 'product-badges__badge--sale');
    imgBadge.setAttribute('hidden', '');
  }
}
 
// Exponer (sin pisar una definición previa idéntica del snippet)
window.__stanleyUpdateCardPrice = window.__stanleyUpdateCardPrice || stanleyUpdateCardPrice;
// ─────────────────────────────────────────────────────────────────────────────
 
// ── Swatch selection fix para Product Recommendations ────────────────────────
// Los scripts de custom-variant-swatches.liquid no se ejecutan cuando
// product-recommendations.js inyecta HTML via innerHTML. Este handler
// replica el comportamiento: actualiza --selected y persiste el variantId.
function initSwatchHandlers(container) {
  const SCROLL_AMOUNT = 108;
 
  container.querySelectorAll('product-swatches, .card-swatches').forEach((wrapper) => {
    if (wrapper._recSwatchInit) return;
    wrapper._recSwatchInit = true;
 
    const track   = wrapper.querySelector('.card-swatches__track');
    const prevBtn = wrapper.querySelector('.card-swatches__nav--prev');
    const nextBtn = wrapper.querySelector('.card-swatches__nav--next');
 
    if (!track) return;
 
    // ── Flechas de navegación ──────────────────────────────────────────────
    function updateNav() {
      if (prevBtn) prevBtn.hidden = track.scrollLeft <= 2;
      if (nextBtn) nextBtn.hidden = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    }
 
    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        track.scrollLeft -= SCROLL_AMOUNT;
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        track.scrollLeft += SCROLL_AMOUNT;
      });
    }
 
    track.addEventListener('scroll', updateNav, { passive: true });
    updateNav();
 
    // ── Swatch --selected y variantId ──────────────────────────────────────
    track.addEventListener('click', (e) => {
      const btn = e.target.closest('.card-swatches__swatch');
      if (!btn) return;
 
      // 1. Actualizar clase --selected
      track.querySelectorAll('.card-swatches__swatch').forEach((s) => {
        s.classList.remove('card-swatches__swatch--selected');
      });
      btn.classList.add('card-swatches__swatch--selected');
 
      // 2. Persistir en el wrapper
      wrapper._selectedMediaId  = btn.dataset.mediaId  || null;
      wrapper._selectedVariantId = btn.dataset.variantId || null;
 
      // 3. Actualizar data-first-variant-id en product-card
      const card = btn.closest('product-card');
      if (card && btn.dataset.variantId) {
        card.dataset.firstVariantId = btn.dataset.variantId;
      }
 
      // 4. STANLEY: actualizar precio tachado + badge según el descuento del swatch.
      //    Sin esto, al cambiar de la variante con descuento a una sin descuento
      //    la card seguía mostrando el precio tachado y el badge -X%.
      const updatePrice = window.__stanleyUpdateCardPrice || stanleyUpdateCardPrice;
      if (card && typeof updatePrice === 'function') {
        updatePrice(card, btn.dataset.variantId);
      }
    });
  });
}
// ─────────────────────────────────────────────────────────────────────────────
 
// ── Botón "Agregar" en Product Recommendations ────────────────────────────────
 
/** Obtiene los section IDs del cart drawer para que el tema refresque el HTML */
function getCartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((node) => {
    const sid = node.dataset.sectionId;
    if (sid && !ids.includes(sid)) ids.push(sid);
  });
  return ids;
}
 
function handleRecAddClick(btn) {
  // El botón puede estar dentro o fuera de product-card según el estado del DOM.
  // Usamos data-card-id como fallback cuando fue movido fuera.
  const card = btn.closest('product-card')
    || (btn.dataset.cardId ? document.getElementById(btn.dataset.cardId) : null);
  if (!card) return;
 
  const selectedSwatch = card.querySelector('.card-swatches__swatch--selected');
  const variantId = selectedSwatch?.dataset?.variantId || card.dataset.firstVariantId;
  if (!variantId) return;
 
  btn.disabled = true;
  const originalText = btn.textContent.trim();
  const sectionIds = getCartSectionIds();
 
  fetch('/cart/add.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
    body: JSON.stringify({
      id: variantId,
      quantity: 1,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    }),
  })
    .then((res) => {
      if (!res.ok) throw new Error('Cart add failed');
      return res.json();
    })
    .then((data) => {
      btn.textContent = '✓';
      btn.classList.add('rec-add-to-cart-btn--added');
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: 'rec-add-btn',
            data: { source: 'rec-add-btn', sections: data.sections || {} },
          },
        })
      );
      setTimeout(() => {
        btn.textContent = originalText;
        btn.classList.remove('rec-add-to-cart-btn--added');
        btn.disabled = false;
      }, 2000);
    })
    .catch(() => {
      btn.textContent = originalText;
      btn.disabled = false;
    });
}
 
function initRecAddButtons(container) {
  // 1. Clonar cada botón para eliminar cualquier listener previo (deploys anteriores)
  container.querySelectorAll('.rec-add-to-cart-btn').forEach((btn) => {
    if (btn._recAddInit) return; // ya inicializado en esta sesión, skip
    const clone = btn.cloneNode(true);
    clone._recAddInit = false;
    btn.parentNode.replaceChild(clone, btn);
  });
 
  // 2. Inicializar swatch handlers para --selected y variantId tracking
  initSwatchHandlers(container);
 
  // 3. Event delegation con capture en cada resource-list__item
  // El capture intercepta el click ANTES de que product-card-link lo consuma.
  container.querySelectorAll('.resource-list__item').forEach((item) => {
    if (item._recAddDelegated) return;
    item._recAddDelegated = true;
 
    item.addEventListener('click', (e) => {
      const btn = e.target.closest('.rec-add-to-cart-btn');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      btn._recAddInit = true;
      handleRecAddClick(btn);
    }, true); // capture phase — antes que product-card-link
  });
 
  // 4. Fix mobile "Combina con": el tema genera padding-right: 50px en .group-block-content
  //    via inline style con !important (aplicado en el connectedCallback de los custom elements).
  //    Como es inline style no puede ser pisado por CSS externo.
  //    Se usa doble requestAnimationFrame para esperar que todos los custom elements
  //    terminen su upgrade cycle y apliquen sus inline styles antes del override.
  //    Si corriera sincrónico, el inline style aún no existiría y el fix no tendría efecto.
  const rec = container.closest('product-recommendations') || container;
  if (rec && rec.dataset.sectionId && rec.dataset.sectionId.includes('__main')) {
    const applyPaddingFix = () => {
      rec.querySelectorAll('.group-block-content').forEach((gc) => {
        gc.style.setProperty('padding-right', '0px', 'important');
        gc.style.setProperty('padding-inline-end', '0px', 'important');
      });
    };
    requestAnimationFrame(() => {
      applyPaddingFix();
      requestAnimationFrame(applyPaddingFix);
    });
  }
}
// ─────────────────────────────────────────────────────────────────────────────